import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
} from "@mui/material";
import UpdateMedicamentVariantForm from "./UpdateMedicamentVariantForm";
import AddMedicamentVariantForm from "./AddMedicamentVariantForm";
import { useParams } from "react-router-dom";
import ConfirmationDialog from "../components/ConfirmationDialog";

const MedicamentVariantList = () => {
  const { medicamentId, specialite } = useParams();
  const [variants, setVariants] = useState([]);
  const [open, setOpen] = useState(false);
  const [currentVariant, setCurrentVariant] = useState(null);

  // Deletion state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [variantToDelete, setVariantToDelete] = useState(null);

  // Duplication state
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [variantToDuplicate, setVariantToDuplicate] = useState(null);

  // Snackbar feedback state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // 1. Wrap fetchVariants in useCallback
  const fetchVariants = useCallback(async () => {
    try {
      const response = await axios.get(`medicament_variants/${medicamentId}`, {
        headers: {
          "x-access-token":
            localStorage.getItem("token") || sessionStorage.getItem("token"),
          Authorization: `Bearer ${
            localStorage.getItem("token") || sessionStorage.getItem("token")
          }`,
        },
      });
      
      // Sort the variants by 'createdAt' in descending order (newest first)
      const sortedVariants = response.data.sort((a, b) => {
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
      
      setVariants(sortedVariants);
    } catch (error) {
      console.error(error);
    }
  }, [medicamentId]); // 2. medicamentId is the only external dependency here

  // 3. Now safely include fetchVariants in the useEffect dependencies
  useEffect(() => {
    fetchVariants();
  }, [fetchVariants, open]);

  const handleAddClick = () => {
    setCurrentVariant(null);
    setOpen(true);
  };

  const handleUpdateClick = (variant) => {
    setCurrentVariant(variant);
    setOpen(true);
  };

  const handleDeleteClick = (id, medicamentId, pathologyId) => {
    setVariantToDelete({ id, medicamentId, pathologyId });
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    setDeleteDialogOpen(false);

    if (!variantToDelete) return;

    const { id, medicamentId, pathologyId } = variantToDelete;

    try {
      await axios.delete(`medicament_variants/${id}`, {
        headers: {
          "x-access-token":
            localStorage.getItem("token") || sessionStorage.getItem("token"),
          Authorization: `Bearer ${
            localStorage.getItem("token") || sessionStorage.getItem("token")
          }`,
        },
        data: {
          medicamentId,
          pathologyId,
        },
      });

      fetchVariants();
      setSnackbar({
        open: true,
        message: "Variant deleted successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Error deleting variant:", error);
      setSnackbar({
        open: true,
        message: "Failed to delete this variant. Please try again.",
        severity: "error",
      });
    }
  };

  const handleDuplicateClick = (id) => {
    setVariantToDuplicate(id);
    setDuplicateDialogOpen(true);
  };

  const confirmDuplicate = async () => {
    setDuplicateDialogOpen(false);

    if (!variantToDuplicate) return;

    try {
      await axios.post(
        `medicament_variants/${variantToDuplicate}/duplicate`,
        {},
        {
          headers: {
            "x-access-token":
              localStorage.getItem("token") || sessionStorage.getItem("token"),
            Authorization: `Bearer ${
              localStorage.getItem("token") || sessionStorage.getItem("token")
            }`,
          },
        }
      );

      fetchVariants(); // Refresh the list after successful duplication
      setSnackbar({
        open: true,
        message: "Variant duplicated successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Error duplicating variant:", error);
      setSnackbar({
        open: true,
        message: "Failed to duplicate this variant. Please try again.",
        severity: "error",
      });
    }
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentVariant(null);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <div>
      <h1>{specialite}: Medicament Variants</h1>
      <Button
        variant="contained"
        color="primary"
        onClick={handleAddClick}
        sx={{ mb: 2 }}
      >
        Add Variant
      </Button>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Medoc ou Complement ou Phyto</TableCell>
              <TableCell>Dose</TableCell>
              <TableCell>Frequence</TableCell>
              <TableCell>Créé le</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {variants.map((variant) => (
              <TableRow key={variant._id}>
                <TableCell>{variant.medoc_ou_complement_ou_phyto}</TableCell>
                <TableCell>{variant.dose}</TableCell>
                <TableCell>{variant.frequence}</TableCell>
                <TableCell>
                  {variant.createdAt
                    ? new Date(variant.createdAt).toLocaleString("en-GB")
                    : "N/A"}
                </TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => handleUpdateClick(variant)}
                  >
                    Update
                  </Button>

                  <Button
                    variant="outlined"
                    color="success"
                    onClick={() => handleDuplicateClick(variant._id)}
                    style={{ margin: "0 8px" }}
                  >
                    Duplicate
                  </Button>

                  <Button
                    onClick={() =>
                      handleDeleteClick(
                        variant._id,
                        variant.medicamentId,
                        variant.pathologyId,
                      )
                    }
                    color="error"
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>
          {currentVariant ? "Update Variant" : "Add Variant"}
        </DialogTitle>
        {currentVariant ? (
          <DialogContent>
            <UpdateMedicamentVariantForm
              variant={currentVariant}
              medicamentId={medicamentId}
              onClose={handleClose}
              onSave={fetchVariants}
            />
          </DialogContent>
        ) : (
          <DialogContent>
            <AddMedicamentVariantForm
              medicamentId={medicamentId}
              onClose={handleClose}
              onSave={fetchVariants}
            />
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Confirm Deletion"
        message="Are you sure you want to delete this variant?"
        confirmText="Delete"
        confirmColor="error"
      />

      {/* Duplicate Confirmation Dialog */}
      <ConfirmationDialog
        open={duplicateDialogOpen}
        onClose={() => setDuplicateDialogOpen(false)}
        onConfirm={confirmDuplicate}
        title="Confirm Duplication"
        message="Are you sure you want to duplicate this variant?"
        confirmText="Duplicate"
        confirmColor="info"
      />

      {/* Action Feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ marginTop: "64px" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default MedicamentVariantList;